/**
 * @file Frontend `Lab` — typed Client for the learner-facing SPA.
 *       Class is intentionally not exposed via the package's exports
 *       map; only `main.tsx` constructs it (relative import).
 * @module @labcraft/lab/src/client
 *
 * Single `CoreClient` export of `@labcraft/lab/src/client` per the
 * rule in `.agents/rules/services.md` § "Workspace Client shape —
 * one `CoreClient`, lifecycle namespacers". `Lab` adopts the two
 * transports (`http` + `ws`) at construction; the lifecycle child-
 * walk drives their configure / start / stop.
 *
 * Three `CoreLifecycle` namespacers, one per UI route surface:
 *
 *   - {@link Account} — `lab.account.*`, AccountMenu + `/account/*`
 *     route family.
 *   - {@link Courses} — `lab.courses.*`, `/courses` catalog reads.
 *   - {@link Chapter} — `lab.chapter.*`, `/chapter/...` route: combined
 *     chapter fetch, engagement switching, per-machine terminal pool.
 *
 * `main.tsx` owns the 401-bounce wiring: it calls
 * `http.on('unauthorized', ...)` with a callback that flips the
 * `qkMe` TanStack entry to `{ learner: null }`, then constructs `Lab`
 * with the same transports. This Client doesn't depend on
 * `@tanstack/react-query` — it's vanilla wire surface, SW-ready.
 *
 * Shares its class name with the backend `Lab` in
 * `../service/index.ts` and the subprocess fixture `Lab` in
 * `../../test/service.ts` by design — the three never appear in the
 * same module, per ADR-010's package-name-as-class-name convention.
 */

import { CoreClient, type CoreTransport } from '@labcraft/core'
import type {
  HttpClientTransport,
  WsClientTransport,
} from '@labcraft/core/frontend'
import {
  ConfigRoute,
  PostReportRoute,
  type LabConfigResponse,
  type ReportRequest,
  type ReportResponse,
} from '../shared/routes.ts'
import { Account } from './account.ts'
import { Chapter } from './chapter.ts'
import { Courses } from './courses.ts'

////////////////////////////////////////////////////////////////////////////////
// Transport seams
////////////////////////////////////////////////////////////////////////////////

/**
 * Structural transport contract every lab namespacer (and the parent)
 * consumes — the `request` + `stream` + `on('unauthorized', ...)` subset of
 * `HttpClientTransport`, intersected with `CoreTransport` so the
 * parent can carry it as a `CoreClient` transport-record value.
 *
 * Both `HttpClientTransport` and `VirtualHostTransport` extend
 * `CoreTransport` and expose the same three methods, so the integration
 * harness constructs `Lab` with `VirtualHostTransport` unchanged.
 */
export type HttpTransport = CoreTransport &
  Pick<HttpClientTransport, 'request' | 'stream'>

/**
 * Structural WebSocket transport contract — the `subscribe` surface of
 * `WsClientTransport`, intersected with `CoreTransport` for the same
 * reason as `HttpTransport`.
 */
export type WsTransport = CoreTransport & Pick<WsClientTransport, 'subscribe'>

////////////////////////////////////////////////////////////////////////////////
// Props + Deps
////////////////////////////////////////////////////////////////////////////////

/**
 * Constructor props for the frontend `Lab`. No knobs beyond the
 * lifecycle id; transport-level configuration lives on the supplied
 * `HttpClientTransport` / `WsClientTransport`.
 *
 * @property id - CoreLifecycle id (default `'lab'`).
 */
export type LabProps = {
  id?: string
}

/**
 * Runtime dependencies. The operator (`main.tsx` for production
 * mounts; the integration harness for tests) constructs the two
 * transports once and threads them through. The `CoreClient` base
 * adopts both at construction so the lifecycle child-walk handles
 * configure / start / stop automatically.
 *
 * @property http - Typed HTTP transport. Stateless; one instance per
 *   page load.
 * @property ws - Typed WebSocket transport. Drives the per-machine
 *   terminal connections under `lab.chapter`.
 */
export type LabDeps = {
  http: HttpTransport
  ws: WsTransport
}

////////////////////////////////////////////////////////////////////////////////
// Lab (frontend Client)
////////////////////////////////////////////////////////////////////////////////

/**
 * Top-level frontend Client for `lab.labcraft.dev`. Single `CoreClient`
 * export of `@labcraft/lab/src/client`.
 *
 * Owns three route-namespacers; logs and metrics bubble under
 * `lab.<route>.*`. The 401 → cache flip lives in `main.tsx`, not
 * here — this Client is React-free.
 */
export class Lab extends CoreClient<
  LabProps,
  { http: HttpTransport; ws: WsTransport }
> {
  readonly account: Account
  readonly courses: Courses
  readonly chapter: Chapter

  constructor(props: LabProps, deps: LabDeps) {
    super(
      { id: props.id ?? 'lab' },
      { transports: { http: deps.http, ws: deps.ws } },
    )
    const http = this.deps.transports.http
    const ws = this.deps.transports.ws
    this.account = this.adopt(new Account(http))
    this.courses = this.adopt(new Courses(http))
    this.chapter = this.adopt(new Chapter(http, ws))
  }

  /**
   * `GET /api/config` — runtime config for the lab SPA bootstrap
   * (analytics tracker config today). Shell-level concern, so it's a
   * flat method on the parent rather than a route namespacer, mirroring
   * www's `Www.config()`. The SPA injects the Umami tracker only when
   * `analytics` is non-null (see `@labcraft/ui`'s `useAnalytics`).
   */
  async config(): Promise<LabConfigResponse> {
    return this.deps.transports.http.request(ConfigRoute)
  }

  /**
   * `POST /api/report` — submit an in-lab issue report (#1672). Shell-level
   * concern (the report affordance lives in the app-bar chrome, not a route
   * namespacer), so it's a flat method on the parent, mirroring `config`.
   * The daemon redacts the message + command tail and emails the report to
   * the operator inbox; the SPA only carries the learner's words, category,
   * and the context it opted to attach.
   *
   * @param args - The report draft + optional attached context.
   * @returns `{ status: 'accepted' }` once the report is accepted for
   *   delivery.
   */
  async reportIssue(args: ReportRequest): Promise<ReportResponse> {
    return this.deps.transports.http.request(PostReportRoute, { body: args })
  }
}
